import React from 'react';
import { Check } from 'react-feather';

/**
 * StepRail — the schema wizard's navigation.
 *
 * Three fixes over the old horizontal <ol>:
 *  1. Steps are addressed by KEY, and numbered by POSITION. The old rail
 *     printed the raw step id, so the AI-confirmed branch (ids 1,2,3,4,6)
 *     showed a step labelled "6" in the fifth slot.
 *  2. Completed steps are BUTTONS — jump straight back to Upload from Save
 *     instead of pressing Back four times.
 *  3. Steps carry their own reachability, so a branch that skips a step
 *     (AI extraction produced database-ready rows → no manual field mapping)
 *     drops it from the rail entirely rather than leaving a dead number.
 *
 * props
 *   steps    [{ key, title, sub?, flag?, complete?, enabled? }]
 *   current  key of the active step
 *   onSelect(key)   omit to make the rail read-only
 */
export default function StepRail({ steps = [], current, onSelect }) {
  if (!steps.length) return null;
  const currentIndex = steps.findIndex((s) => s.key === current);
  return (
    <nav className="ep-rail" aria-label="Schema builder steps">
      {steps.map((step, i) => {
        const isCurrent = step.key === current;
        // "done" is what the rail paints orange: explicitly complete, or simply
        // behind us in a linear flow.
        const done = step.complete != null ? step.complete : (currentIndex > -1 && i < currentIndex);
        const reachable = step.enabled != null ? step.enabled : (done || isCurrent);
        const clickable = !!onSelect && reachable && !isCurrent;
        return (
          <button
            key={step.key}
            type="button"
            className={`ep-step${isCurrent ? ' ep-current' : ''}${done ? ' ep-done' : ''}`}
            onClick={clickable ? () => onSelect(step.key) : undefined}
            disabled={!clickable}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <span className="ep-step-mark" aria-hidden="true">
              {done && !isCurrent ? <Check size={12} /> : i + 1}
            </span>
            <span className="ep-step-text">
              <span className="ep-step-title">{step.title}</span>
              {step.sub ? <span className="ep-step-sub">{step.sub}</span> : null}
            </span>
            {step.flag ? <span className="ep-step-flag">{step.flag}</span> : null}
          </button>
        );
      })}
    </nav>
  );
}
